import values from 'lodash/values'
import { routePaths } from './routePaths'
import { btoa, toQueryParams } from '@ztechapps/common.utils'
import type { QueryFilter, ViewLayoutType } from './types'
import type { PartialDeep } from 'type-fest'

export type ProductListPageSearchParams = {
  view?: ViewLayoutType
  search?: string
} & PartialDeep<QueryFilter>

export const routesDef = {
  HomePage: {
    name: 'Home',
    path: routePaths.HomePage,
    altPath: () => {
      return '/'
    },
    exact: true,
    layout: 'main',
  },
  ProductListPage: {
    name: 'Search products',
    path: routePaths.ProductListPage,
    altPath: (options?: ProductListPageSearchParams) => {
      const query = toQueryParams(options)

      return `/search/list/?${query}`
    },
    exact: true,
    layout: 'main',
  },
  ProductItemPage: {
    name: 'Product item',
    path: routePaths.ProductItemPage,
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)
      return `/search/${_id}`
    },
    exact: true,
    layout: 'main',
  },
  PostListPage: {
    name: 'Post list',
    path: routePaths.PostListPage,
    exact: true,
    layout: 'main',
  },
  PostItemPage: {
    name: 'Post item',
    path: routePaths.PostItemPage,
    altPath: (id) => {
      return `/post/${encodeURIComponent(id)}`
    },
    exact: true,
    layout: 'main',
  },

  CartPage: {
    name: 'Cart',
    path: routePaths.CartPage,
    altPath: (options = {}) => {
      return `/cart/?${new URLSearchParams(options).toString()}`
    },
    exact: true,
    layout: 'private',
  },

  //checkout landing page
  CheckoutMessageSent: {
    name: 'Checkout Message Sent',
    path: routePaths.CheckoutMessageSent,
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)
      return `/checkout/message-sent/${_id}`
    },
    exact: true,
    layout: 'main',
  },
  CheckoutSuccess: {
    name: 'Checkout Success',
    path: routePaths.CheckoutSuccess,
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)
      return `/checkout/success/${_id}`
    },
    exact: true,
    layout: 'main',
  },
  CheckoutCancel: {
    name: 'Checkout Cancel',
    path: routePaths.CheckoutCancel,
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)
      return `/checkout/cancel/${_id}`
    },
    exact: true,
    layout: 'main',
  },

  UseAndCareListPage: {
    name: 'Use and Care list',
    path: routePaths.UseAndCareListPage,
    altPath: () => {
      return `/use-and-care`
    },
    exact: true,
    layout: 'main',
  },
  UseAndCareItemPage: {
    name: 'Use and Care',
    path: routePaths.UseAndCareItemPage,
    altPath: (id) => {
      return `/use-and-care/${encodeURIComponent(id)}`
    },
    exact: true,
    layout: 'main',
  },
  AccountBoardsPage: {
    name: 'Boards',
    path: routePaths.AccountBoardsPage,
    altPath: () => {
      return `/account/boards`
    },
    exact: true,
    layout: 'main',
  },
  AccountBoardsDetailsPage: {
    name: 'Boards Details',
    path: routePaths.AccountBoardsDetailsPage,
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)
      return `/account/boards/board-detail/${_id}`
    },
    exact: true,
    layout: 'main',
  },
  AccountOverview: {
    name: 'Account Overview',
    path: routePaths.AccountOverview,
    altPath: () => {
      return '/account/overview'
    },
    exact: true,
    layout: 'private',
  },

  AccountOrders: {
    name: 'Account Orders',
    path: routePaths.AccountOrders,
    altPath: ({ activeTab = 'all' } = {}) => {
      activeTab = encodeURIComponent(activeTab)
      return `/account/orders/?activeTab=${activeTab}`
    },
    exact: true,
    layout: 'private',
  },
  AccountOrderItem: {
    name: 'Account item',
    path: routePaths.AccountOrderItem,
    exact: true,
    layout: 'private',
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)
      return `/account/orders/${_id}`
    },
  },

  AccountMessages: {
    name: 'Account Messages',
    path: routePaths.AccountMessages,
    exact: true,
    layout: 'private',
  },

  AccountMessageDetails: {
    name: 'Account Messages',
    path: routePaths.AccountMessageDetails,
    exact: true,
    layout: 'private',
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)
      return `/account/messages/${_id}`
    },
  },

  AccountSettings: {
    name: 'Account Settings',
    path: routePaths.AccountSettings,
    exact: true,
    layout: 'private',
  },

  //The invoice page for showing the invoices and make it ready for payment
  InvoicePage: {
    name: 'Invoice',
    path: routePaths.InvoicePage,
    altPath: ({ filter, accessToken }) => {
      const params = encodeURIComponent(btoa({ filter, accessToken }))
      return `/invoice?params=${params}`
    },
    exact: true,
    layout: 'blank',
  },
  InvoiceCheckoutPage: {
    name: 'Invoice Checkout',
    path: routePaths.InvoiceCheckoutPage,
    altPath: ({ filter, accessToken }) => {
      const params = encodeURIComponent(btoa({ filter, accessToken }))
      return `/invoice/checkout?params=${params}`
    },
    exact: true,
    layout: 'blank',
  },

  //The read-only invoice a customer opens from an emailed link: no sign-in, no pay button
  InvoiceViewPage: {
    name: 'Invoice',
    path: routePaths.InvoiceViewPage,
    altPath: ({ token }: { token: string }) => `/invoice/view/${encodeURIComponent(token)}`,
    exact: true,
    layout: 'blank',
  },
  //The one state shown for a link that is expired, revoked or unknown
  InvoiceViewExpiredPage: {
    name: 'Invoice link expired',
    path: routePaths.InvoiceViewExpiredPage,
    altPath: () => '/invoice/expired',
    exact: true,
    layout: 'blank',
  },
  LoginPage: {
    name: 'Login',
    path: routePaths.LoginPage,
    altPath: (options: { redirect_url?: string } = {}) => {
      return `/account/login?${new URLSearchParams(options).toString()}`
    },
    exact: true,
    layout: 'main',
  },

  JoinPage: {
    name: 'Login',
    path: routePaths.JoinPage,
    altPath: () => {
      return `/account/join`
    },
    exact: true,
    layout: 'blank',
  },
  RegisterPage: {
    name: 'Register',
    path: routePaths.RegisterPage,
    altPath: '/account/register',
    exact: true,
    layout: 'main',
  },

  //needs to update the user registration
  UpdateUserRegistration: {
    name: 'UpdateUserRegistration',
    path: routePaths.UpdateUserRegistration,
    altPath: () => '/account/update-registration',
    exact: true,
    layout: 'main',
  },

  ChangePasswordPage: {
    name: 'ChangePassword',
    path: routePaths.ChangePasswordPage,
    altPath: '/account/change-password',
    exact: true,
    layout: 'main',
  },

  AuthSuccessPage: {
    name: 'Auth Success',
    path: routePaths.AuthSuccessPage,
    altPath: (params: { accessToken: string; userId: string; orderId?: string; updateNeeded?: boolean }) => {
      const data = btoa(params)
      return `/auth/success/${data}`
    },

    exact: true,
    layout: 'blank',
  },
  AuthFailedPage: {
    name: 'Auth Failed',
    path: routePaths.AuthFailedPage,
    exact: true,
    layout: 'blank',
  },
}
export const routesArray = values(routesDef)
